import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';
import { useNotifications } from './useNotifications';
import { asStoredDeclaration, campaignLaunchDecision } from '../../supabase/functions/_shared/marketingConsent';

export interface Campaign {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  status: string;
  campaign_type?: 'inbound' | 'outbound';
  target_audience?: any;
  call_script?: string;
  retell_agent_id?: string;
  agent_id?: string | null;
  total_leads: number;
  calls_made: number;
  calls_connected: number;
  calls_completed: number;
  conversion_rate?: number;
  created_at: string;
  updated_at: string;
  routing_config?: any;
  business_hours?: any;
  voicemail_config?: any;
  contact_cap?: number | null;
  contacts_used?: number;
  contact_attempts?: number;
  contact_slots?: Array<{ lead_id: string; attempts: number; connected: boolean }>;
}

export const useCampaigns = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { logCampaignEvent } = useNotifications();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchCampaigns();
    }
  }, [user]);

  const fetchCampaigns = async () => {
    if (!user) return;

    try {
      setLoading(true);
      console.log('Fetching campaigns for user:', user.id);
      
      const { data, error } = await supabase
        .from('campaigns')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching campaigns:', error);
        console.log('User ID:', user.id);
        console.log('Error details:', { code: error.code, message: error.message, details: error.details });
        toast({
          title: "Error",
          description: "Failed to fetch campaigns",
          variant: "destructive",
        });
        return;
      }

      console.log('Campaigns fetched successfully:', data);
      const rows = data || [];
      const ids = rows.map((row) => row.id);
      const slotsByCampaign = new Map<string, Array<{ lead_id: string; attempts: number; connected: boolean }>>();
      if (ids.length > 0) {
        const { data: slotRows, error: slotError } = await supabase
          .from('campaign_contact_slots')
          .select('campaign_id, lead_id, attempts, connected')
          .in('campaign_id', ids);
        if (slotError) {
          console.error('Contact slots unavailable', slotError);
        } else {
          for (const slot of slotRows ?? []) {
            const list = slotsByCampaign.get(slot.campaign_id) ?? [];
            list.push({
              lead_id: slot.lead_id,
              attempts: slot.attempts,
              connected: slot.connected,
            });
            slotsByCampaign.set(slot.campaign_id, list);
          }
        }
      }
      setCampaigns(rows.map((row) => {
        const slots = slotsByCampaign.get(row.id) ?? [];
        return {
          ...row,
          contact_cap: row.contact_cap ?? null,
          contacts_used: slots.length,
          contact_attempts: slots.reduce((sum, slot) => sum + (slot.attempts || 0), 0),
          contact_slots: slots,
        } as Campaign;
      }));
    } catch (error) {
      console.error('Error in fetchCampaigns:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createCampaign = async (campaignData: Omit<Campaign, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('campaigns')
        .insert({
          ...campaignData,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating campaign:', error);
        toast({
          title: "Error",
          description: "Failed to create campaign",
          variant: "destructive",
        });
        throw error;
      }

      setCampaigns(prev => [data as Campaign, ...prev]);
      toast({
        title: "Success",
        description: "Campaign created successfully",
      });
      logCampaignEvent(data.name, 'created');
      return data;
    } catch (error) {
      console.error('Error in createCampaign:', error);
      throw error;
    }
  };

  const updateCampaign = async (campaignId: string, updates: Partial<Campaign>) => {
    if (!user) return;

    try {
      if (updates.status === 'active') {
        const db = supabase as unknown as { from: (table: string) => any };
        const { data: declarations, error: declarationError } = await db
          .from('lawful_basis_declarations')
          .select('*')
          .eq('user_id', user.id)
          .limit(20);
        if (declarationError) {
          toast({
            title: "Cannot launch",
            description: "The lawful-basis declaration could not be checked, so the campaign was not started.",
            variant: "destructive",
          });
          throw declarationError;
        }
        const rows = Array.isArray(declarations) ? declarations : [];
        const latest = rows.slice().sort((a, b) => String(b.declared_at ?? "").localeCompare(String(a.declared_at ?? "")))[0] ?? null;
        const launch = campaignLaunchDecision(latest ? asStoredDeclaration(latest) : null, user.id);
        if (!launch.allowed) {
          toast({
            title: "Declaration required",
            description: launch.message,
            variant: "destructive",
          });
          throw new Error(launch.message);
        }
      }

      const { data, error } = await supabase
        .from('campaigns')
        .update(updates)
        .eq('id', campaignId)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating campaign:', error);
        toast({
          title: "Error",
          description: /LAWFUL_BASIS_REQUIRED/i.test(error.message ?? "")
            ? "Confirm the lawful basis and where the contact list came from before launching this campaign."
            : "Failed to update campaign",
          variant: "destructive",
        });
        throw error;
      }

      setCampaigns(prev => prev.map(campaign => 
        campaign.id === campaignId ? (data as Campaign) : campaign
      ));
      toast({
        title: "Success",
        description: "Campaign updated successfully",
      });
      
      // Log specific campaign status changes
      if (updates.status === 'active') {
        logCampaignEvent(data.name, 'started');
      } else if (updates.status === 'paused') {
        logCampaignEvent(data.name, 'paused');
      } else if (updates.status === 'completed') {
        logCampaignEvent(data.name, 'completed');
      }
      
      return data;
    } catch (error) {
      console.error('Error in updateCampaign:', error);
      throw error;
    }
  };

  const deleteCampaign = async (campaignId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('campaigns')
        .delete()
        .eq('id', campaignId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error deleting campaign:', error);
        toast({
          title: "Error",
          description: "Failed to delete campaign",
          variant: "destructive",
        });
        throw error;
      }

      const deletedCampaign = campaigns.find(c => c.id === campaignId);
      setCampaigns(prev => prev.filter(campaign => campaign.id !== campaignId));
      toast({
        title: "Success",
        description: "Campaign deleted successfully",
      });
      if (deletedCampaign) {
        logCampaignEvent(deletedCampaign.name, 'deleted');
      }
    } catch (error) {
      console.error('Error in deleteCampaign:', error);
      throw error;
    }
  };

  return {
    campaigns,
    loading,
    createCampaign,
    updateCampaign,
    deleteCampaign,
    refetch: fetchCampaigns
  };
};